// Guarda.tsx — /t/<codice>/guarda: chi è uscito in semifinale guarda la finale (i due video, il conto alla
// rovescia, il risultato). Non gioca: niente fotocamera, niente microfono. Il video arriva direttamente dai telefoni
// dei finalisti (gioco/spettatori.ts); il server manda solo lo stato della partita.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Faccina } from "../componenti/Faccina.tsx";
import { Visione } from "../gioco/spettatori.ts";
import { tempoReale } from "../servizi/tempoReale.ts";
import { idDispositivo } from "../servizi/dispositivo.ts";
import { vai } from "../servizi/percorso.ts";
import { serverIceSpettatore, statoTorneo, tokenTorneo } from "../servizi/tornei.ts";
import type { ServerIce } from "../servizi/sfide.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { PERCORSO_TORNEO } from "../costanti.ts";
import type { Ruolo, VistaPartita } from "../../../condiviso/partita.ts";

const RUOLI: readonly Ruolo[] = ["a", "b"];

type Stato = { p: "caricamento" } | { p: "guarda"; partita: string; ice: ServerIce[] } | { p: "fine" };
/** Stanza chiusa mentre si guarda (es. finalisti non ancora entrati): si ricontrolla il torneo dopo un attimo. */
const RICONTROLLO_MS = 4_000;

export function Guarda({ codice, tema }: { codice: string; tema: ControlloTema }) {
  const { t } = useTranslation();
  const [stato, setStato] = useState<Stato>({ p: "caricamento" });
  const [vista, setVista] = useState<VistaPartita | null>(null);
  const [flussi, setFlussi] = useState<Record<Ruolo, MediaStream | null>>({ a: null, b: null });
  const [audio, setAudio] = useState(false);
  const [tentativo, setTentativo] = useState(0);
  const torna = () => vai(`${PERCORSO_TORNEO}${codice}`, true);

  // La finale da guardare: il torneo dice il codice; i server per il video sono quelli dello spettatore.
  useEffect(() => {
    const token = tokenTorneo(codice);
    if (!token) {
      torna();
      return;
    }
    let fermo = false;
    statoTorneo(codice, token)
      .then(async (v) => {
        if (!v.guarda) {
          if (!fermo) setStato({ p: "fine" });
          return;
        }
        const ice = await serverIceSpettatore(codice, token, v.guarda.codice).catch(() => ({ iceServers: [] as ServerIce[], turn: false }));
        if (!fermo) setStato({ p: "guarda", partita: v.guarda.codice, ice: ice.iceServers });
      })
      .catch(() => !fermo && setStato({ p: "fine" }));
    return () => {
      fermo = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codice, tentativo]);

  // La stanza si è chiusa: se la finale è ancora da guardare (i finalisti non erano ancora entrati) si rientra.
  const riprova = () => {
    setVista(null);
    setFlussi({ a: null, b: null });
    setStato({ p: "caricamento" });
    setTimeout(() => setTentativo((n) => n + 1), RICONTROLLO_MS);
  };

  // Canale in tempo reale: si entra come spettatore (anche dopo ogni ricollegamento).
  const visioni = useRef<Partial<Record<Ruolo, Visione>>>({});
  const partita = stato.p === "guarda" ? stato.partita : null;
  const ice = stato.p === "guarda" ? stato.ice : null;
  useEffect(() => {
    if (!partita || !ice) return;
    const token = tokenTorneo(codice);
    if (!token) return;
    for (const r of RUOLI) {
      visioni.current[r] = new Visione({
        invia: (d) => void tempoReale.invia({ tipo: "segnale", dati: d, con: r }),
        suFlusso: (s) => setFlussi((f) => ({ ...f, [r]: s })),
        suStato: () => undefined,
      });
    }
    const stacca = tempoReale.ascolta((m) => {
      if (m.tipo === "partita") setVista(m.vista);
      else if (m.tipo === "segnale" && (m.da === "a" || m.da === "b")) void visioni.current[m.da]?.ricevi(m.dati);
      else if (m.tipo === "guarda_fine" || (m.tipo === "errore" && m.codice === "sfida_non_trovata")) riprova();
      else if (m.tipo === "errore" && (m.codice === "bloccato" || m.codice === "sostituito")) setStato({ p: "fine" });
    });
    const staccaCollegamento = tempoReale.alCollegamento(() => {
      tempoReale.invia({ tipo: "guarda", codice: partita, token, dispositivo: idDispositivo() });
    });
    return () => {
      stacca();
      staccaCollegamento();
      tempoReale.invia({ tipo: "esci" });
      for (const r of RUOLI) visioni.current[r]?.chiudi();
      visioni.current = {};
    };
  }, [partita, ice, codice]);

  // Il video di un finalista si (ri)chiede quando è collegato e a ogni suo rientro (contatore delle sessioni).
  const sessioneA = vista?.collegati.a ? vista.sessioni.a : null;
  const sessioneB = vista?.collegati.b ? vista.sessioni.b : null;
  useEffect(() => {
    if (sessioneA !== null && ice) void visioni.current.a?.avvia(ice);
  }, [sessioneA, ice]);
  useEffect(() => {
    if (sessioneB !== null && ice) void visioni.current.b?.avvia(ice);
  }, [sessioneB, ice]);

  if (stato.p === "fine") {
    return (
      <div className="pagina schermata-semplice">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo">{t("torneo.finale_finita")}</p>
        <button type="button" className="pulsante pulsante-primario" onClick={torna}>
          {t("torneo.torna")}
        </button>
      </div>
    );
  }
  if (stato.p === "caricamento" || !vista) {
    return (
      <div className="pagina schermata-semplice" aria-busy="true">
        <Faccina tema={tema.tema} />
        <p className="schermata-testo">{t("generale.caricamento")}</p>
      </div>
    );
  }

  const e = vista.esito;
  let titolo: string;
  if (vista.fase === "risultato" && e) titolo = e.vincitore ? t("torneo.guarda_ha_vinto", { nome: vista.nomi[e.vincitore] }) : t("gioco.pareggio");
  else if (vista.fase === "gioco" || vista.fase === "verifica" || vista.fase === "accusa") titolo = t("torneo.guarda_in_gioco");
  else if (vista.fase === "conto") titolo = t("gioco.via");
  else if (vista.fase === "pausa") titolo = t("torneo.guarda_pausa");
  else titolo = t("torneo.guarda_attesa");

  return (
    <div className="pagina guarda">
      <header className="guarda-testa">
        <p className="incontro-titolo">{t("torneo.turno_finale")}</p>
        <p className="pannello-titolo" role="status" aria-live="polite">
          {titolo}
        </p>
      </header>
      <div className="guarda-video">
        {RUOLI.map((r) => (
          <figure key={r} className={`guarda-riquadro ${e?.vincitore === r && vista.fase === "risultato" ? "guarda-vincitore" : ""}`} data-ruolo={r}>
            <VideoRemoto flusso={flussi[r]} audio={audio} />
            {!flussi[r] && <span className="guarda-attesa">{vista.collegati[r] ? t("gioco.video_collegamento") : t("gioco.in_attesa_di", { nome: vista.nomi[r] })}</span>}
            <figcaption>{vista.nomi[r]}</figcaption>
          </figure>
        ))}
      </div>
      <div className="azioni-riga guarda-azioni">
        <button type="button" className="pulsante pulsante-piccolo" aria-pressed={audio} onClick={() => setAudio((a) => !a)}>
          {audio ? t("torneo.audio_spento") : t("torneo.audio_acceso")}
        </button>
        <button type="button" className="pulsante pulsante-piccolo" onClick={torna}>
          {t("torneo.torna")}
        </button>
      </div>
    </div>
  );
}

function VideoRemoto({ flusso, audio }: { flusso: MediaStream | null; audio: boolean }) {
  const rif = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (rif.current && rif.current.srcObject !== flusso) rif.current.srcObject = flusso;
  }, [flusso]);
  return <video ref={rif} autoPlay playsInline muted={!audio} />;
}
